/**
 * Tour catalogs. Steps use `[data-tour="…"]` selectors.
 * Bump `version` when steps change so existing users see the updated tour once.
 */

export const TOUR_IDS = {
  APP_SHELL: "app-shell",
  IMAGE_STUDIO: "image-studio",
  VIDEO_STUDIO: "video-studio",
  AI_CLIPPING: "ai-clipping",
  MUSIC: "music",
  SCHEDULER: "scheduler",
};

/** Map dashboard `?page=` values → auto-start tour id */
export const PAGE_TO_TOUR = {
  "explore-ideas": TOUR_IDS.APP_SHELL,
  dashboards: TOUR_IDS.APP_SHELL,
  influencers: TOUR_IDS.IMAGE_STUDIO,
  "video-studio": TOUR_IDS.VIDEO_STUDIO,
  "ai-clipping": TOUR_IDS.AI_CLIPPING,
  music: TOUR_IDS.MUSIC,
  "social-scheduler": TOUR_IDS.SCHEDULER,
};

/** Preferred page to open when replaying a tour from Help */
export const TOUR_TO_PAGE = {
  [TOUR_IDS.APP_SHELL]: "explore-ideas",
  [TOUR_IDS.IMAGE_STUDIO]: "influencers",
  [TOUR_IDS.VIDEO_STUDIO]: "video-studio",
  [TOUR_IDS.AI_CLIPPING]: "ai-clipping",
  [TOUR_IDS.MUSIC]: "music",
  [TOUR_IDS.SCHEDULER]: "social-scheduler",
};

export const TOUR_META = {
  [TOUR_IDS.APP_SHELL]: {
    id: TOUR_IDS.APP_SHELL,
    version: 1,
    titleKey: "tourAppShellTitle",
    titleFallback: "Welcome tour",
    descriptionKey: "tourAppShellDesc",
    descriptionFallback: "A quick walk through the workspace navigation and credits.",
  },
  [TOUR_IDS.IMAGE_STUDIO]: {
    id: TOUR_IDS.IMAGE_STUDIO,
    version: 1,
    titleKey: "tourImageStudioTitle",
    titleFallback: "Image Studio tour",
    descriptionKey: "tourImageStudioDesc",
    descriptionFallback: "Learn how to prompt, attach references, pick a model, and generate.",
  },
  [TOUR_IDS.VIDEO_STUDIO]: {
    id: TOUR_IDS.VIDEO_STUDIO,
    version: 1,
    titleKey: "tourVideoStudioTitle",
    titleFallback: "Video Studio tour",
    descriptionKey: "tourVideoStudioDesc",
    descriptionFallback: "Generate clips, review output, and join them on the Clip board.",
  },
  [TOUR_IDS.AI_CLIPPING]: {
    id: TOUR_IDS.AI_CLIPPING,
    version: 1,
    titleKey: "tourAiClippingTitle",
    titleFallback: "AI Clipping tour",
    descriptionKey: "tourAiClippingDesc",
    descriptionFallback: "Turn long videos into short clips, then find them in My clips.",
  },
  [TOUR_IDS.MUSIC]: {
    id: TOUR_IDS.MUSIC,
    version: 1,
    titleKey: "tourMusicTitle",
    titleFallback: "Music Studio tour",
    descriptionKey: "tourMusicDesc",
    descriptionFallback: "Write or generate lyrics, compose a track, and browse your library.",
  },
  [TOUR_IDS.SCHEDULER]: {
    id: TOUR_IDS.SCHEDULER,
    version: 1,
    titleKey: "tourSchedulerTitle",
    titleFallback: "Scheduler tour",
    descriptionKey: "tourSchedulerDesc",
    descriptionFallback: "Link social accounts, pick media, write a caption, and schedule posts.",
  },
};

/**
 * @param {Record<string, string>} [lang]
 */
export function buildTourSteps(tourId, lang = {}) {
  const t = (key, fallback) => lang?.[key] || fallback;

  switch (tourId) {
    case TOUR_IDS.APP_SHELL:
      return [
        {
          target: "body",
          placement: "center",
          disableBeacon: true,
          content: t(
            "tourAppShellWelcome",
            "Welcome to BeyundAI — one workspace for image, video, and more. Let’s take a quick look around.",
          ),
          title: t("tourAppShellWelcomeTitle", "Welcome"),
        },
        {
          target: '[data-tour="nav-influencers"]',
          placement: "right",
          content: t(
            "tourAppShellImageNav",
            "Image Studio — generate and edit stills with the latest AI image models.",
          ),
          title: t("tourAppShellImageNavTitle", "Image Studio"),
        },
        {
          target: '[data-tour="nav-video-studio"]',
          placement: "right",
          content: t(
            "tourAppShellVideoNav",
            "Video Studio — create clips, then join them on the Clip board.",
          ),
          title: t("tourAppShellVideoNavTitle", "Video Studio"),
        },
        {
          target: '[data-tour="nav-explore-ideas"]',
          placement: "right",
          content: t(
            "tourAppShellExploreNav",
            "Explore Ideas — pick a ready-made concept and recreate it in one click.",
          ),
          title: t("tourAppShellExploreNavTitle", "Explore Ideas"),
        },
        {
          target: '[data-tour="topbar-credits"]',
          placement: "bottom",
          content: t(
            "tourAppShellCredits",
            "Your credit balance lives here. Generations spend credits — tap Billing when you need more.",
          ),
          title: t("tourAppShellCreditsTitle", "Credits"),
        },
        {
          target: '[data-tour="nav-help"]',
          placement: "right",
          content: t(
            "tourAppShellHelp",
            "Need a hand later? Open Help anytime — you can also restart these tours from there.",
          ),
          title: t("tourAppShellHelpTitle", "Help"),
        },
      ];

    case TOUR_IDS.IMAGE_STUDIO:
      return [
        {
          target: '[data-tour="image-studio-composer"]',
          placement: "top",
          disableBeacon: true,
          content: t(
            "tourImageComposer",
            "Describe the image you want here. Tip: skip “give me a prompt…” — just describe the scene.",
          ),
          title: t("tourImageComposerTitle", "Prompt"),
        },
        {
          target: '[data-tour="image-studio-attach"]',
          placement: "top",
          content: t(
            "tourImageAttach",
            "Upload reference images, then type @ in the prompt to tag them for edits.",
          ),
          title: t("tourImageAttachTitle", "References"),
        },
        {
          target: '[data-tour="image-studio-model"]',
          placement: "top",
          content: t(
            "tourImageModel",
            "Pick Auto for the latest best model, or choose a specific model yourself.",
          ),
          title: t("tourImageModelTitle", "Model"),
        },
        {
          target: '[data-tour="image-studio-settings"]',
          placement: "top",
          content: t(
            "tourImageSettings",
            "Set aspect ratio and related options before you generate.",
          ),
          title: t("tourImageSettingsTitle", "Settings"),
        },
        {
          target: '[data-tour="image-studio-generate"]',
          placement: "top",
          content: t(
            "tourImageGenerate",
            "Hit send to start generation. Results appear in the canvas and history rail.",
          ),
          title: t("tourImageGenerateTitle", "Generate"),
        },
        {
          target: '[data-tour="image-studio-history"]',
          placement: "left",
          content: t(
            "tourImageHistory",
            "Your recent generations stay in this rail — click one to reopen it.",
          ),
          title: t("tourImageHistoryTitle", "History"),
        },
      ];

    case TOUR_IDS.VIDEO_STUDIO:
      return [
        {
          target: '[data-tour="video-studio-form"]',
          placement: "right",
          disableBeacon: true,
          content: t(
            "tourVideoForm",
            "Choose a model, add media if needed, write your prompt, and set duration / scenes.",
          ),
          title: t("tourVideoFormTitle", "Create"),
        },
        {
          target: '[data-tour="video-studio-generate"]',
          placement: "top",
          content: t(
            "tourVideoGenerate",
            "Generate your clip. Multi-scene plans can generate scene-by-scene, then join automatically.",
          ),
          title: t("tourVideoGenerateTitle", "Generate"),
        },
        {
          target: '[data-tour="video-studio-output"]',
          placement: "left",
          content: t(
            "tourVideoOutput",
            "Preview finished clips here — share, regenerate, or send them to the Clip board.",
          ),
          title: t("tourVideoOutputTitle", "Preview"),
        },
        {
          target: '[data-tour="video-studio-clip-board"]',
          placement: "top",
          content: t(
            "tourVideoClipBoard",
            "Clip board sits at the bottom. Add clips from your library, drag to reorder, then Join videos.",
          ),
          title: t("tourVideoClipBoardTitle", "Clip board"),
        },
      ];

    case TOUR_IDS.AI_CLIPPING:
      return [
        {
          target: '[data-tour="ai-clipping-tabs"]',
          placement: "bottom",
          disableBeacon: true,
          content: t(
            "tourAiClippingTabs",
            "Switch between Create clips (new jobs) and My clips (your finished shorts library).",
          ),
          title: t("tourAiClippingTabsTitle", "Create vs library"),
        },
        {
          target: '[data-tour="ai-clipping-form"]',
          placement: "right",
          content: t(
            "tourAiClippingForm",
            "Paste a YouTube URL or hosted file, pick templates/assets, then set the clip window.",
          ),
          title: t("tourAiClippingFormTitle", "Source & options"),
        },
        {
          target: '[data-tour="ai-clipping-generate"]',
          placement: "top",
          content: t(
            "tourAiClippingGenerate",
            "Generate clips runs in the background — you can leave the page; we’ll notify you when they’re ready.",
          ),
          title: t("tourAiClippingGenerateTitle", "Generate"),
        },
        {
          target: '[data-tour="ai-clipping-faq"]',
          placement: "left",
          content: t(
            "tourAiClippingFaq",
            "Quick answers about what AI Clipping supports and how credits work.",
          ),
          title: t("tourAiClippingFaqTitle", "FAQ"),
        },
        {
          target: '[data-tour="ai-clipping-tab-library"]',
          placement: "bottom",
          content: t(
            "tourAiClippingLibraryTab",
            "Open My clips anytime to preview, copy captions, share, or download finished shorts.",
          ),
          title: t("tourAiClippingLibraryTabTitle", "My clips"),
        },
      ];

    case TOUR_IDS.MUSIC:
      return [
        {
          target: '[data-tour="music-tabs"]',
          placement: "bottom",
          disableBeacon: true,
          content: t(
            "tourMusicTabs",
            "Studio is where you compose. Library keeps your songs and saved lyrics.",
          ),
          title: t("tourMusicTabsTitle", "Studio & library"),
        },
        {
          target: '[data-tour="music-compose"]',
          placement: "right",
          content: t(
            "tourMusicCompose",
            "Pick a model, genre, and style — tune vocal/instrumental options here.",
          ),
          title: t("tourMusicComposeTitle", "Composition"),
        },
        {
          target: '[data-tour="music-lyrics"]',
          placement: "left",
          content: t(
            "tourMusicLyrics",
            "Paste your own lyrics or generate them with AI, then edit before making the track.",
          ),
          title: t("tourMusicLyricsTitle", "Lyrics"),
        },
        {
          target: '[data-tour="music-generate"]',
          placement: "top",
          content: t(
            "tourMusicGenerate",
            "Hit Generate to create the song. Results land in your Music library when ready.",
          ),
          title: t("tourMusicGenerateTitle", "Generate"),
        },
        {
          target: '[data-tour="music-tab-library"]',
          placement: "bottom",
          content: t(
            "tourMusicLibraryTab",
            "Browse finished tracks and lyrics here — reuse a lyric back into Studio anytime.",
          ),
          title: t("tourMusicLibraryTabTitle", "Library"),
        },
      ];

    case TOUR_IDS.SCHEDULER:
      return [
        {
          target: '[data-tour="scheduler-tabs"]',
          placement: "bottom",
          disableBeacon: true,
          content: t(
            "tourSchedulerTabs",
            "Schedule Post is for publishing. Link Accounts is where you connect TikTok, Instagram, YouTube, and Facebook.",
          ),
          title: t("tourSchedulerTabsTitle", "Two tabs"),
        },
        {
          target: '[data-tour="scheduler-form"]',
          placement: "bottom",
          content: t(
            "tourSchedulerForm",
            "Choose video or character content, then pick media from your library.",
          ),
          title: t("tourSchedulerFormTitle", "New post"),
        },
        {
          target: '[data-tour="scheduler-caption"]',
          placement: "top",
          content: t(
            "tourSchedulerCaption",
            "Write a caption or use AI Generate for a creator-style caption with hashtags.",
          ),
          title: t("tourSchedulerCaptionTitle", "Caption"),
        },
        {
          target: '[data-tour="scheduler-platforms"]',
          placement: "top",
          content: t(
            "tourSchedulerPlatforms",
            "Select which linked platforms get this post. Locked chips mean that account isn’t connected yet.",
          ),
          title: t("tourSchedulerPlatformsTitle", "Platforms"),
        },
        {
          target: '[data-tour="scheduler-submit"]',
          placement: "top",
          content: t(
            "tourSchedulerSubmit",
            "Set the date/time, then schedule. Pending posts show in the history below.",
          ),
          title: t("tourSchedulerSubmitTitle", "Schedule"),
        },
        {
          target: '[data-tour="scheduler-tab-connect"]',
          placement: "bottom",
          content: t(
            "tourSchedulerConnectTab",
            "Link Accounts — connect platforms once so Schedule Post can publish to them.",
          ),
          title: t("tourSchedulerConnectTabTitle", "Link Accounts"),
        },
      ];

    default:
      return [];
  }
}

export function getTourMeta(tourId) {
  return TOUR_META[tourId] || null;
}

export function resolveTourIdForPage(pageId) {
  return PAGE_TO_TOUR[pageId] || null;
}
